import React, { useState, useEffect, useCallback } from 'react';
import { getDepartamentosPaginados, deleteDepartamento } from '../services/departamentoService';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, ArrowLeft, Navigation, Search, Edit, Trash2 } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';

export default function DepartamentosList() {
    const [departamentos, setDepartamentos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');

    const navigate = useNavigate();

    const cargarDepartamentos = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getDepartamentosPaginados(page, pageSize, searchTerm);
            setDepartamentos(data.content);
            setTotalPages(data.totalPages);
        } catch (error) {
            toast.error('Error al cargar los departamentos');
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, searchTerm]);

    useEffect(() => {
        cargarDepartamentos();
    }, [cargarDepartamentos]);

    const handleEliminar = async (id) => {
        if (window.confirm('¿Desea eliminar este departamento?')) {
            try {
                await deleteDepartamento(id);
                toast.success('Departamento eliminado');
                cargarDepartamentos();
            } catch (error) {
                toast.error(error.message);
            }
        }
    };

    return (
        <div className="max-w-4xl mx-auto bg-white shadow-sm border border-gray-200 mb-6 font-sans">
            <Toaster position="top-right" />
            <div className="bg-slate-700 p-3 flex justify-between items-center text-white">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/parametrizacion')} className="p-1 hover:bg-slate-600 rounded">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-2">
                        <Navigation className="w-5 h-5 text-emerald-400" />
                        <h1 className="text-lg font-bold tracking-tight uppercase">Departamentos</h1>
                    </div>
                </div>
                <Link to="/parametrizacion/departamentos/nuevo" className="flex items-center gap-1 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-sm">
                    <PlusCircle className="w-4 h-4" /> NUEVO DEPTO
                </Link>
            </div>

            <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input 
                        type="text" 
                        placeholder="Buscar por nombre..." 
                        className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-sm text-sm focus:border-emerald-500 outline-none"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-50 border-b-2 border-slate-200 text-slate-600 text-[0.65rem] font-black uppercase tracking-widest">
                            <th className="px-6 py-4 w-20 text-center">ID</th>
                            <th className="px-6 py-4">Nombre del Departamento</th>
                            <th className="px-6 py-4 text-center w-40">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm">
                        {loading ? (
                            <tr><td colSpan="3" className="px-6 py-10 text-center text-slate-400 font-bold uppercase tracking-widest text-xs">Cargando datos...</td></tr>
                        ) : departamentos.length === 0 ? (
                            <tr><td colSpan="3" className="px-6 py-10 text-center text-slate-400 font-bold uppercase tracking-widest text-xs">No se encontraron registros</td></tr>
                        ) : (
                            departamentos.map((d) => (
                                <tr key={d.id} className="border-b border-slate-100 hover:bg-emerald-50/30 transition-colors">
                                    <td className="px-6 py-3 text-center font-mono text-xs text-slate-400">{d.id}</td>
                                    <td className="px-6 py-3 font-bold text-slate-800 uppercase text-xs">{d.nombre}</td>
                                    <td className="px-6 py-3 text-center">
                                        <div className="flex justify-center gap-4">
                                            <Link to={`/parametrizacion/departamentos/${d.id}/editar`} className="text-blue-600 hover:text-blue-800 text-[0.65rem] font-black underline flex items-center gap-1">
                                                <Edit className="w-3 h-3" /> EDITAR
                                            </Link>
                                            <button onClick={() => handleEliminar(d.id)} className="text-rose-500 hover:text-rose-700 text-[0.65rem] font-black underline flex items-center gap-1">
                                                <Trash2 className="w-3 h-3" /> QUITAR
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="p-3 bg-white flex justify-end gap-1 border-t border-slate-200">
                <button onClick={() => setPage(page-1)} disabled={page === 0} className="px-4 py-1.5 bg-slate-100 border text-[0.65rem] font-black disabled:opacity-30">PREV</button>
                <button onClick={() => setPage(page+1)} disabled={page >= totalPages - 1} className="px-4 py-1.5 bg-slate-800 text-white text-[0.65rem] font-black disabled:opacity-30">NEXT</button>
            </div>
        </div>
    );
}
